import { Location } from 'history';
import AppPaths from '../AppPaths';
import { webApiClient } from './fi-api/apiUtils';
import { logInternalError } from './errors';

const VERIZON_CACHE_KEY = 'verizon_validated';
const VERIZON_EXPIRATION_KEY = 'verizon_validated_expires';
const VERIZON_TOKEN_KEY = 'verizon_pdp_token';
const CACHE_DURATION_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

/**
 * Checks if there's a valid cached Verizon validation
 */
function getCachedVerizonStatus(): boolean | null {
  try {
    const cachedStatus = localStorage.getItem(VERIZON_CACHE_KEY);
    const cachedExpiration = localStorage.getItem(VERIZON_EXPIRATION_KEY);

    if (cachedStatus === 'true' && cachedExpiration) {
      const expirationTime = parseInt(cachedExpiration, 10);
      if (Date.now() < expirationTime) {
        return true;
      } else {
        // Cache expired, clear it
        clearVerizonCache();
      }
    }

    return null;
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'cache-read-error'],
      tags: { operation: 'getCachedVerizonStatus' },
    });
    return null;
  }
}

/**
 * Stores a successful Verizon validation in cache, along with the validated token so checkout can
 * send it on the purchase mutation (the backend resolves it to Verizon's hashed user ID and
 * attaches it to the Recurly subscription).
 */
function setCachedVerizonStatus(token: string): void {
  try {
    localStorage.setItem(VERIZON_CACHE_KEY, 'true');
    localStorage.setItem(VERIZON_TOKEN_KEY, token);
    const expirationTime = Date.now() + CACHE_DURATION_MS;
    localStorage.setItem(VERIZON_EXPIRATION_KEY, expirationTime.toString());
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'cache-write-error'],
      tags: { operation: 'setCachedVerizonStatus' },
    });
  }
}

/**
 * Clears the Verizon validation cache
 */
function clearVerizonCache(): void {
  try {
    localStorage.removeItem(VERIZON_CACHE_KEY);
    localStorage.removeItem(VERIZON_EXPIRATION_KEY);
    localStorage.removeItem(VERIZON_TOKEN_KEY);
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'cache-clear-error'],
      tags: { operation: 'clearVerizonCache' },
    });
  }
}

/**
 * Returns the validated Verizon PDP token for the purchase mutation, or null when there is no
 * unexpired validation. Gated on the same expiration as the validated flag so a stale token isn't
 * attached to purchases long after the Verizon visit.
 */
export function getVerizonPdpToken(): string | null {
  try {
    if (getCachedVerizonStatus() !== true) {
      return null;
    }
    return localStorage.getItem(VERIZON_TOKEN_KEY);
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'token-read-error'],
      tags: { operation: 'getVerizonPdpToken' },
    });
    return null;
  }
}

/**
 * Validates a Verizon token via API
 */
async function validateVerizonToken(token: string): Promise<boolean> {
  try {
    const response = await webApiClient.post('/verizon/v1/pdp/token/validate', { token });
    return response.data.valid || false;
  } catch (error) {
    logInternalError(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['verizon-validation', 'api-error'],
      tags: { operation: 'validateVerizonToken' },
    });
    throw error;
  }
}

/**
 * Determines if the current user is from Verizon
 * Checks cache first, then validates token if provided, with fallback logic
 */
async function checkVerizonStatus(token: string | null, fallbackCondition: boolean): Promise<boolean> {
  // If token is provided, always validate it (bypassing cache)
  if (token) {
    try {
      const isValid = await validateVerizonToken(token);

      if (isValid) {
        // Update cache with new validation result
        setCachedVerizonStatus(token);
      } else {
        // Clear cache if token is invalid
        clearVerizonCache();
      }

      return isValid;
    } catch (error) {
      // API failed, use fallback
      return fallbackCondition;
    }
  }

  // No token provided, check cache first
  const cachedStatus = getCachedVerizonStatus();
  if (cachedStatus === true) {
    return true;
  }
  return fallbackCondition;
}

export async function validateVerizonStatus(location: Location, isInternalTester?: boolean) {
  if (location.pathname.includes(AppPaths.Verizon.Root)) {
    if (isInternalTester) {
      // Allow all internal testers
      return true;
    } else {
      // Check Verizon status
      try {
        // Extract token from URL parameters
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');

        // Fallback condition for when no token is provided or API fails
        const fallbackCondition = false;

        return checkVerizonStatus(token, fallbackCondition);
      } catch (error) {
        logInternalError(error instanceof Error ? error : new Error(String(error)), {
          fingerprint: ['verizon-validation', 'context-error'],
          tags: {
            operation: 'validateVerizonStatus',
            pathname: location.pathname,
            hasToken: !!new URLSearchParams(location.search).get('token'),
          },
        });

        return false;
      }
    }
  }
}
